/**
 * Assign / change a key role — the dialog behind the Key-roles "Assign" button
 * and the filled slot's swap action.
 *
 * Built to FORM_STYLE_GUIDE.md (tokens + primitives from `theme/formStyles` and
 * `components/forms/FormPrimitives`): pinned head, scrolling body, FormFooter.
 *
 * EVERY path here is a real backend call — nothing is faked client-side:
 *
 *   role field        `loanRole`, options verbatim from the LoanContact model
 *                     enum (selectOptionsContactRole)
 *   From directory    the loan's own contacts, plus a LIVE lookup through
 *                     POST /underwriting/search_loan_contacts (EV + LQ loans +
 *                     Salesforce). That endpoint needs something searchable, so
 *                     with an empty box the list is the loan's own people and
 *                     the search line says so.
 *   New contact       POST /underwriting/add_loan_related_contact with the role
 *                     already set.
 *   Assign a person
 *   already on the    POST /underwriting/update_loan_related_contact/{id} with
 *   loan              { loanRole } — the same write the edit sidebar makes.
 *   Assign a person
 *   from the          POST /underwriting/add_loan_related_contact with the
 *   directory         picked party projected onto the contact fields.
 *
 * Replacing a filled role: `loanRole` is a single string on the model, so two
 * contacts CAN hold one role — the backend allows it and the slot shows
 * "+n more". Rather than decide silently, the dialog offers one checkbox to
 * clear the outgoing holder's role ({ loanRole: null }, a real update). It does
 * NOT delete them; removing someone from the loan is the slot's own X, which
 * confirms first.
 *
 * Every call returns the updated loan → `loanDrilldownSet`.
 */
import CloseRounded from "@mui/icons-material/CloseRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import axios from "axios";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import {
  isFirmResult,
  mapLoanContactToContactValues,
} from "../../../../../components/loanContactSearch/loanContactResultMapping";
import {
  FormComboField,
  FormField,
  FormFieldGrid,
  FormFooter,
  FormNote,
  FormPhoneField,
  FormSegmented,
  FormTextField,
} from "../../../../../components/forms/FormPrimitives";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { selectOptionsContactRole } from "../../../../../constants/selectOptions/selectOptionsContactRole";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import { FORM, formSx, px } from "../../../../../theme/formStyles";
import { formatPhone } from "../../../../../_src_shared/utils/functions/formatPhone";
import { contactDisplayName } from "./KeyRoleSlotCard";

const MODES = { DIRECTORY: "directory", NEW: "new" };

/** The endpoint refuses anything shorter (guide §4: 2+ letters or digits). */
const MIN_QUERY = 2;
const DEBOUNCE_MS = 400;

const keyOf = (candidate) =>
  (candidate.emailAddress || "").toLowerCase().trim() ||
  `${candidate.name}|${candidate.company || ""}`.toLowerCase();

/** An on-loan contact → the dialog's candidate shape. */
const candidateFromContact = (c) => ({
  id: String(c._id),
  source: "loan",
  contact: c,
  name: contactDisplayName(c),
  company: c?.company || "",
  emailAddress: c?.emailAddress || "",
  phone: c?.phone || "",
  roles: c?.loanRole ? [c.loanRole] : [],
});

/** A search party → the dialog's candidate shape. */
const candidateFromResult = (r, i) => {
  const mapped = mapLoanContactToContactValues(r);
  return {
    id: `search-${r?.partyId || r?.displayName || i}-${i}`,
    source: "directory",
    result: r,
    mapped,
    name: (r?.displayName || "").trim() || "(unnamed)",
    company: isFirmResult(r) ? "" : mapped.company,
    emailAddress: mapped.emailAddress,
    phone: mapped.phone,
    roles: r?.roles || [],
  };
};

const initialsOf = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

/** One selectable person — radio, avatar, name over meta, optional tag. */
const CandidateRow = ({ candidate, selected, onSelect, tag }) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    sx={{
      ...formSx.listRow(selected),
      font: "inherit",
      textAlign: "left",
      width: "100%",
      border: `1px solid ${selected ? FORM.color.accent : "transparent"}`,
      borderRadius: px(FORM.radius.control),
      "& + &": { borderTop: "none" },
    }}
  >
    <Flx
      center
      sx={{
        flexShrink: 0,
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: `1.5px solid ${selected ? FORM.color.accent : FORM.color.ink4}`,
      }}
    >
      {selected && (
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: FORM.color.accent,
          }}
        />
      )}
    </Flx>

    <Flx
      center
      sx={{
        flexShrink: 0,
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: FORM.color.accentTint,
        color: FORM.color.accentStrong,
        fontSize: 10.5,
        fontWeight: 700,
      }}
    >
      {initialsOf(candidate.name)}
    </Flx>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Txt sx={{ ...formSx.body, fontWeight: 500, display: "block" }}>
        {candidate.name}
      </Txt>
      <Txt
        sx={{
          ...formSx.meta,
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {[
          candidate.company,
          candidate.emailAddress,
          candidate.phone ? formatPhone(candidate.phone) : null,
        ]
          .filter(Boolean)
          .join("  ·  ") || "—"}
      </Txt>
    </Box>

    {tag ? (
      <Box component="span" sx={{ ...formSx.pill("info"), flexShrink: 0 }}>
        {tag}
      </Box>
    ) : null}
  </Box>
);

const Group = ({ label, children }) => (
  <Box sx={{ mb: px(FORM.s.s3) }}>
    <Txt sx={{ ...formSx.groupLabel, mb: px(FORM.s.s2) }}>{label}</Txt>
    <Box role="radiogroup" aria-label={label}>
      {children}
    </Box>
  </Box>
);

const ContactRoleAssignDialog = ({
  open,
  role: initialRole,
  currentContact,
  contacts,
  dealId,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    addLoanRelatedContact,
    updateLoanRelatedContact,
    searchLoanContacts,
  } = useUnderwritingHook();

  const [role, setRole] = useState(initialRole || null);
  const [mode, setMode] = useState(MODES.DIRECTORY);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [releaseCurrent, setReleaseCurrent] = useState(true);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const cancelRef = useRef(null);
  // Monotonic request id — a slow earlier search must never overwrite a
  // newer one's results.
  const seqRef = useRef(0);

  // Re-opened on a different role → start clean.
  useEffect(() => {
    if (!open) return;
    setRole(initialRole || null);
    setMode(MODES.DIRECTORY);
    setQuery("");
    setPicked(null);
    setReleaseCurrent(true);
    setResults([]);
    setSearchError("");
    setDraft({});
  }, [open, initialRole]);

  /* ── the live directory lookup ─────────────────────────────────────────
   * Search-as-you-type with the guide's rules: 400 ms debounce, never call
   * below 2 characters, cancel the in-flight request, and keep the 400
   * ("nothing searchable") message under the box rather than in a toast. */
  useEffect(() => {
    if (!open || mode !== MODES.DIRECTORY) return undefined;
    const q = query.trim();
    if (q.length < MIN_QUERY) {
      cancelRef.current?.cancel();
      seqRef.current += 1;
      setResults([]);
      setSearching(false);
      setSearchError("");
      return undefined;
    }
    const timer = setTimeout(() => {
      cancelRef.current?.cancel();
      const source = axios.CancelToken.source();
      cancelRef.current = source;
      const seq = ++seqRef.current;
      setSearching(true);
      setSearchError("");
      searchLoanContacts({
        data: { q, ...(role ? { role } : {}), limit: 25 },
        cancelToken: source.token,
        onSuccessFn: (data) => {
          if (seq !== seqRef.current) return;
          setResults(data?.results || []);
        },
        onFailFn: (err) => {
          if (axios.isCancel(err) || seq !== seqRef.current) return;
          setResults([]);
          setSearchError(
            err?.response?.data?.error ||
              err?.response?.data?.detail ||
              "The directory could not be searched.",
          );
        },
        // Cleared here rather than per-outcome so a cancelled request (which
        // returns through onFailFn) can't leave the spinner running.
        onCompleteFn: () => {
          if (seq === seqRef.current) setSearching(false);
        },
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, query, role]);

  useEffect(() => () => cancelRef.current?.cancel(), []);

  /* ── candidates ────────────────────────────────────────────────────────
   * The loan's own people first (they are already here — assigning one is a
   * single field update), then whatever the directory turned up, deduped on
   * email so a person on the loan isn't offered twice. */
  const { matching, others } = useMemo(() => {
    const onLoan = (contacts || [])
      .filter((c) => c && String(c._id) !== String(currentContact?._id))
      .map(candidateFromContact);
    const seen = new Set(onLoan.map(keyOf));
    const fromSearch = (results || [])
      .map(candidateFromResult)
      .filter((c) => {
        const k = keyOf(c);
        if (!k || seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    const all = [...onLoan, ...fromSearch];
    return {
      matching: role ? all.filter((c) => c.roles.includes(role)) : [],
      others: role ? all.filter((c) => !c.roles.includes(role)) : all,
    };
  }, [contacts, results, role, currentContact]);

  const draftValid = Boolean(
    (draft.firstName || "").trim() ||
      (draft.lastName || "").trim() ||
      (draft.company || "").trim(),
  );
  const canSubmit = Boolean(
    role && !saving && (mode === MODES.NEW ? draftValid : picked),
  );

  const setField = useCallback(
    (name) => (value) => setDraft((d) => ({ ...d, [name]: value })),
    [],
  );

  /** Promisified so the release + assign writes run in order. */
  const call = useCallback(
    (fn, args) =>
      new Promise((resolve, reject) => {
        fn({ ...args, onSuccessFn: resolve, onFailFn: reject });
      }),
    [],
  );

  const handleAssign = useCallback(async () => {
    if (!canSubmit || !dealId) return;
    setSaving(true);
    try {
      // 1 — hand the role off, when asked. A cleared role leaves the contact
      // on the loan with no role, which the model allows (loanRole is
      // nullable); it never deletes them.
      const replacing =
        currentContact?._id &&
        releaseCurrent &&
        (mode === MODES.NEW || picked?.source !== "loan"
          ? true
          : String(picked?.contact?._id) !== String(currentContact._id));
      if (replacing) {
        await call(updateLoanRelatedContact, {
          dealId,
          contactId: currentContact._id,
          contactData: { loanRole: null },
        });
      }

      // 2 — the assignment itself.
      let loan;
      if (mode === MODES.NEW) {
        loan = await call(addLoanRelatedContact, {
          dealId,
          contactData: {
            firstName: (draft.firstName || "").trim() || null,
            lastName: (draft.lastName || "").trim() || null,
            company: (draft.company || "").trim() || null,
            emailAddress: (draft.emailAddress || "").trim() || null,
            phone: (draft.phone || "").trim() || null,
            loanRole: role,
          },
        });
      } else if (picked.source === "loan") {
        loan = await call(updateLoanRelatedContact, {
          dealId,
          contactId: picked.contact._id,
          contactData: { loanRole: role },
        });
      } else {
        const { loanRole: _suggested, ...fields } = picked.mapped;
        loan = await call(addLoanRelatedContact, {
          dealId,
          contactData: {
            ...Object.fromEntries(
              Object.entries(fields).filter(([, v]) => v !== "" && v != null),
            ),
            loanRole: role,
          },
        });
      }

      if (loan) dispatch(loanDrilldownSet(loan));
      enqueueSnackbar(
        `${mode === MODES.NEW ? (draft.firstName || draft.company || "Contact").trim() : picked.name} assigned as ${role}.`,
        { variant: "success" },
      );
      onClose?.();
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.detail || err?.message || "Could not assign.",
        { variant: "error" },
      );
    } finally {
      setSaving(false);
    }
  }, [
    canSubmit,
    dealId,
    currentContact,
    releaseCurrent,
    mode,
    picked,
    draft,
    role,
    call,
    updateLoanRelatedContact,
    addLoanRelatedContact,
    dispatch,
    enqueueSnackbar,
    onClose,
  ]);

  const roleOptions = useMemo(
    () => [...selectOptionsContactRole].sort((a, b) => a.localeCompare(b)),
    [],
  );

  const isChange = Boolean(currentContact);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { ...formSx.paper, maxWidth: 560 } }}
    >
      {/* head — pinned */}
      <Box sx={formSx.head}>
        <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Txt sx={formSx.title}>
              {isChange ? `Change ${initialRole}` : `Assign ${initialRole}`}
            </Txt>
            <Txt sx={formSx.titleHelper}>
              Pick someone already on the loan, search the directory, or add a
              new contact.
            </Txt>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{ mt: px(-4), mr: px(-8) }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
      </Box>

      {/* body — scrolls */}
      <DialogContent sx={{ ...formSx.shellBody, py: px(FORM.s.s5) }}>
        <Box sx={{ mb: px(FORM.s.s5) }}>
          <FormFieldGrid>
            <FormComboField
              label="Role"
              span={6}
              required
              value={role}
              onChange={setRole}
              options={roleOptions}
              helperText="The value stored on the contact — the list is the loan model's own role enum."
            />
          </FormFieldGrid>
        </Box>

        <Box sx={{ mb: px(FORM.s.s4) }}>
          <FormSegmented
            ariaLabel="Where the contact comes from"
            value={mode}
            onChange={(v) => v && setMode(v)}
            options={[
              { value: MODES.DIRECTORY, label: "From directory", accent: true },
              { value: MODES.NEW, label: "New contact" },
            ]}
          />
        </Box>

        {mode === MODES.DIRECTORY ? (
          <>
            <TextField
              size="small"
              fullWidth
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, company or email"
              inputProps={{ "aria-label": "Search the contact directory" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRounded
                      sx={{ fontSize: 16, color: FORM.color.ink4 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={14} thickness={5} />
                  </InputAdornment>
                ) : null,
              }}
              sx={{ ...formSx.input, mb: px(FORM.s.s3) }}
            />

            {searchError ? (
              <Txt
                sx={{
                  ...formSx.helper,
                  mt: 0,
                  mb: px(FORM.s.s3),
                  color: FORM.color.required,
                }}
              >
                {searchError}
              </Txt>
            ) : query.trim().length < MIN_QUERY ? (
              <Txt sx={{ ...formSx.helper, mt: 0, mb: px(FORM.s.s3) }}>
                Showing the people already on this loan. Type two or more
                characters to search every loan and Salesforce.
              </Txt>
            ) : null}

            {matching.length > 0 && (
              <Group label={`Usually works as ${role}`}>
                {matching.map((c) => (
                  <CandidateRow
                    key={c.id}
                    candidate={c}
                    selected={picked?.id === c.id}
                    onSelect={() => setPicked(c)}
                    tag={c.source === "loan" ? "On loan" : null}
                  />
                ))}
              </Group>
            )}

            {others.length > 0 && (
              <Group label={matching.length > 0 ? "Everyone else" : "Contacts"}>
                {others.map((c) => (
                  <CandidateRow
                    key={c.id}
                    candidate={c}
                    selected={picked?.id === c.id}
                    onSelect={() => setPicked(c)}
                    tag={c.source === "loan" ? "On loan" : null}
                  />
                ))}
              </Group>
            )}

            {matching.length === 0 && others.length === 0 && !searching && (
              <Txt sx={{ ...formSx.helper, mt: 0 }}>
                {query.trim().length >= MIN_QUERY
                  ? "Nothing matched. Add them as a new contact instead."
                  : "No other contacts on this loan yet."}
              </Txt>
            )}
          </>
        ) : (
          <FormFieldGrid>
            <FormTextField
              label="First name"
              span={3}
              value={draft.firstName || ""}
              onChange={setField("firstName")}
            />
            <FormTextField
              label="Last name"
              span={3}
              value={draft.lastName || ""}
              onChange={setField("lastName")}
            />
            <FormTextField
              label="Company"
              span={6}
              value={draft.company || ""}
              onChange={setField("company")}
            />
            <FormTextField
              label="Email"
              span={3}
              type="email"
              value={draft.emailAddress || ""}
              onChange={setField("emailAddress")}
            />
            <FormPhoneField
              label="Phone"
              span={3}
              value={draft.phone || ""}
              onChange={setField("phone")}
            />
            <FormField span={6}>
              <Txt sx={{ ...formSx.helper, mt: 0 }}>
                Need the address, license or bank details? Save here, then open
                the contact to fill the rest.
              </Txt>
            </FormField>
          </FormFieldGrid>
        )}

        {/* Replacing a filled role — the one decision this dialog can't make. */}
        {isChange && (
          <Box sx={{ mt: px(FORM.s.s5) }}>
            <FormNote>
              <Box
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: px(FORM.s.s2),
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  size="small"
                  checked={releaseCurrent}
                  onChange={(e) => setReleaseCurrent(e.target.checked)}
                  sx={{ ...formSx.checkbox, mt: "-2px" }}
                />
                <Box>
                  Clear {contactDisplayName(currentContact)}&apos;s{" "}
                  {initialRole} role. They stay on the loan — untick this and
                  both will hold the role.
                </Box>
              </Box>
            </FormNote>
          </Box>
        )}
      </DialogContent>

      <FormFooter
        status={
          saving ? (
            <Flx ac g={1}>
              <CircularProgress size={13} thickness={5} />
              Saving…
            </Flx>
          ) : null
        }
      >
        <Button
          variant="outlined"
          color="gray"
          onClick={onClose}
          disabled={saving}
          sx={{ ...formSx.button, textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleAssign}
          disabled={!canSubmit}
          sx={{ ...formSx.button, textTransform: "none" }}
        >
          {mode === MODES.NEW ? "Add and assign" : "Assign"}
        </Button>
      </FormFooter>
    </Dialog>
  );
};

ContactRoleAssignDialog.propTypes = {
  open: PropTypes.bool,
  /** The role being filled. */
  role: PropTypes.string,
  /** Who holds it now, when this is a change rather than a first assignment. */
  currentContact: PropTypes.object,
  /** The loan's contacts — the first place to look. */
  contacts: PropTypes.array,
  dealId: PropTypes.string,
  onClose: PropTypes.func,
};

export default ContactRoleAssignDialog;
